import React, { useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './App.scss';
import AppLayout from './layouts/AppLayout';
import RosterView from './features/roster/views/RosterView';
import ClassListView from './features/roster/views/ClassListView';
import TeacherDashboardLayout from './features/roster/dashboard/views/TeacherDashboardLayout';
import UpcomingExamsView from './features/roster/dashboard/views/UpcomingExamsView';
import RoomGanttView from './features/roster/dashboard/views/RoomGanttView';
import MonitoringView from './features/roster/dashboard/views/MonitoringView';
import ImportHistoryView from './features/roster/import/views/ImportHistoryView';
import ShareLinksView from './features/roster/share/views/ShareLinksView';
import { LandingPage } from './features/landing';
import { useAuth } from './features/auth';
import { SharedClassPage } from './features/share-public';

interface ProtectedRouteProps {
  isAuthenticated: boolean;
  children: React.ReactElement;
}

/**
 * Bảo vệ route yêu cầu đăng nhập trước khi truy cập.
 * @param isAuthenticated Trạng thái đăng nhập hiện tại.
 * @param children Nội dung route cần bảo vệ.
 * @returns Component con nếu đã đăng nhập, ngược lại chuyển về trang landing.
 */
function ProtectedRoute({ isAuthenticated, children }: ProtectedRouteProps) {
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Đọc tham số route/query để hiển thị trang chia sẻ công khai.
 * @returns Component trang chia sẻ với id/exp/sig lấy từ URL hiện tại.
 */
function SharedClassRoute() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  return <SharedClassPage id={id} exp={searchParams.get('exp') || ''} sig={searchParams.get('sig') || ''} />;
}

/**
 * Component chính của ứng dụng Sổ ảnh sinh viên
 */
function App() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async ({ email, password }: { email: string; password: string }) => {
    const isValidHustEmail = /.+@hust\.edu\.vn$/i.test(email);

    if (!isValidHustEmail) {
      throw new Error('Vui lòng dùng email HUST có định dạng @hust.edu.vn.');
    }

    if (password.length < 6) {
      throw new Error('Mật khẩu cần có ít nhất 6 ký tự.');
    }

    try {
      await login({ email, password });
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 401) {
        throw new Error('Sai email hoặc mật khẩu. Vui lòng kiểm tra lại.');
      }

      if (status === 400) {
        throw new Error('Email hoặc mật khẩu không đúng định dạng backend yêu cầu.');
      }

      if (error?.code === 'ECONNABORTED' || !error?.response) {
        throw new Error('Không thể kết nối tới server xác thực. Vui lòng thử lại sau.');
      }

      throw new Error('Đăng nhập thất bại. Vui lòng thử lại.');
    }

    navigate('/classes', { replace: true });
  };

  return (
    <>
      <Routes>
        <Route path="/classes/shared/:id" element={<SharedClassRoute />} />

        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/classes" replace />
            ) : (
              <LandingPage onSubmit={handleLogin} />
            )
          }
        />

        <Route element={<ProtectedRoute isAuthenticated={isAuthenticated}><AppLayout /></ProtectedRoute>}>
          <Route path="/classes" element={<ClassListView />} />
          <Route path="/classes/:classId" element={<RosterView />} />
          <Route path="/dashboard" element={<TeacherDashboardLayout />}>
            <Route index element={<Navigate to="upcoming" replace />} />
            <Route path="upcoming" element={<UpcomingExamsView />} />
            <Route path="gantt" element={<RoomGanttView />} />
            <Route path="monitoring" element={<MonitoringView />} />
          </Route>
          <Route path="/import-history" element={<ImportHistoryView />} />
          <Route path="/share" element={<ShareLinksView />} />
        </Route>

        <Route path="*" element={<Navigate to={isAuthenticated ? '/classes' : '/'} replace />} />
      </Routes>
    </>
  );
}

export default App;
